import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import Footer from './Footer';

const GuideLayout = ({ title, subtitle, eyebrow, readTime, lastUpdated, children }) => {
  const { t } = useTranslation();
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = `${title} · Guide · AEO Orchestra`;
      window.scrollTo(0, 0);
    }
  }, [title]);

  return (
    <>
      <Header />
      <main className="pt-24 pb-24 bg-[#0a0a0a] min-h-screen">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/guide" className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-[#0055FF] mb-8 text-sm transition-colors">
            <ArrowLeft size={16} /> {t('guides.backToGuides')}
          </Link>

          <div className="mb-8">
            {eyebrow && (
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0055FF]/10 border border-[#0055FF]/20 text-[#0055FF] text-xs font-medium uppercase tracking-wider mb-4">
                <Tag size={12} /> {eyebrow}
              </div>
            )}
            <h1 className="font-['Outfit'] text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight mb-4">
              {title}
            </h1>
            {subtitle && (
              <p className="text-lg text-[#A1A1AA] leading-relaxed">{subtitle}</p>
            )}
            <div className="flex items-center gap-4 mt-5 text-xs text-[#666]">
              {readTime && (
                <span className="inline-flex items-center gap-1">
                  <Clock size={12} /> {readTime} {t('guides.readTime')}
                </span>
              )}
              {lastUpdated && <span>{t('guides.updatedOn')} {lastUpdated}</span>}
            </div>
          </div>

          <article className="orch-guide-article prose prose-invert max-w-none">
            {children}
          </article>

          <div className="mt-16 pt-8 border-t border-white/10">
            <h3 className="font-['Outfit'] text-xl font-semibold text-white mb-3">{t('guides.feedbackTitle')}</h3>
            <p className="text-[#A1A1AA] mb-5">{t('guides.feedbackText')}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0055FF] hover:bg-[#3377FF] text-white font-medium text-sm transition-colors">
                {t('guides.startFree')}
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-white/15 hover:border-white/30 text-white font-medium text-sm transition-colors">
                {t('guides.contactUs')}
              </Link>
              <Link to="/guide" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-[#A1A1AA] hover:text-white font-medium text-sm transition-colors">
                {t('guides.allGuides')}
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />

      <style>{`
        .orch-guide-article h2 {
          font-family: 'Outfit', sans-serif;
          font-size: 1.6rem;
          font-weight: 700;
          color: #fff;
          margin: 2.5rem 0 1rem;
          padding-left: 1rem;
          border-left: 4px solid #0055FF;
        }
        .orch-guide-article h3 {
          font-family: 'Outfit', sans-serif;
          font-size: 1.2rem;
          font-weight: 600;
          color: #fff;
          margin: 2rem 0 0.75rem;
        }
        .orch-guide-article p {
          color: #C8C8CC;
          font-size: 1rem;
          line-height: 1.75;
          margin: 0 0 1.25rem;
        }
        .orch-guide-article ul, .orch-guide-article ol {
          color: #C8C8CC;
          font-size: 1rem;
          line-height: 1.75;
          margin: 0 0 1.25rem 1.5rem;
        }
        .orch-guide-article li { margin-bottom: 0.5rem; }
        .orch-guide-article strong { color: #fff; font-weight: 600; }
        .orch-guide-article code {
          background: rgba(0,85,255,0.1);
          color: #6ea8ff;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.9em;
        }
        .orch-guide-article pre {
          background: #111;
          border: 1px solid rgba(255,255,255,0.1);
          padding: 16px;
          border-radius: 8px;
          overflow-x: auto;
          margin: 1.5rem 0;
        }
        .orch-guide-article blockquote {
          border-left: 3px solid #0055FF;
          padding: 0.75rem 1.25rem;
          margin: 1.5rem 0;
          background: rgba(0,85,255,0.05);
          border-radius: 0 6px 6px 0;
          color: #ddd;
          font-style: italic;
        }
        .orch-guide-article a { color: #0055FF; }
        .orch-guide-article a:hover { color: #3377FF; }
        .orch-guide-article img {
          max-width: 100%;
          border-radius: 10px;
          margin: 1.5rem 0;
          border: 1px solid rgba(255,255,255,0.08);
        }
      `}</style>
    </>
  );
};

export default GuideLayout;
